"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useTranslations } from "next-intl"
import { usePathname } from "@/i18n/navigation"
import { Link } from "@/i18n/navigation"
import { BarChart3, UserX, Banknote, Cookie } from "lucide-react"

const EXCLUDED_PATHS = ['/privacy', '/terms', '/gtc', '/beta-terms']

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void
  }
}

export function CookieConsent() {
  const [open, setOpen] = useState(false)
  const pathname = usePathname()
  const t = useTranslations("cookies")

  const updateGoogleConsent = (status: 'granted' | 'denied') => {
    if (typeof window !== "undefined" && typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'ad_storage': status,
        'ad_user_data': status,
        'ad_personalization': status,
        'analytics_storage': status
      });
    }
  }

  useEffect(() => {
    // Don't show on legal pages - users should be able to read privacy policy without interruption
    const isExcludedPath = EXCLUDED_PATHS.some(path => pathname?.startsWith(path))
    if (isExcludedPath) {
      return
    }

    // Check existing consent on mount
    const consent = localStorage.getItem("cookieConsent")
    
    if (consent === "accepted") {
      updateGoogleConsent("granted")
    } else if (!consent) {
      // Show modal if no consent has been given yet
      const timer = setTimeout(() => setOpen(true), 1000)
      return () => clearTimeout(timer)
    }
    // If consent was rejected, we don't show the modal again
  }, [pathname])

  const handleAccept = () => {
    localStorage.setItem("cookieConsent", "accepted")
    updateGoogleConsent("granted") // Enable tracking
    setOpen(false)
  }

  const handleReject = () => {
    localStorage.setItem("cookieConsent", "rejected")
    updateGoogleConsent("denied") // Ensure tracking stays disabled
    setOpen(false)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent 
        className="sm:max-w-lg w-full max-w-[calc(100%-1rem)] p-0 gap-0 border shadow-2xl" 
        showCloseButton={false}
        // Prevent closing by clicking outside or pressing escape to force a choice
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Header with Background */}
        <div className="bg-muted/30 px-6 pt-5 pb-4">
          <DialogHeader className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                <Cookie className="h-5 w-5 text-accent" />
              </div>
              <DialogTitle className="text-xl leading-tight">{t("title")}</DialogTitle>
            </div>
            <DialogDescription className="text-sm leading-relaxed text-foreground/80 pr-2">
              {t("description")}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 pb-5 space-y-5">
          {/* Checklist Box */}
          <div className="grid gap-3 p-4 bg-muted/20 rounded-xl border border-border/40">
            <div className="flex items-center gap-3 text-sm font-medium text-foreground/90">
              <div className="h-6 w-6 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                <BarChart3 className="h-3.5 w-3.5 text-accent" />
              </div>
              <span className="wrap-break-word">{t("points.conversion")}</span>
            </div>
            <div className="flex items-center gap-3 text-sm font-medium text-foreground/90">
              <div className="h-6 w-6 rounded-full bg-green-500/10 flex items-center justify-center shrink-0">
                <UserX className="h-3.5 w-3.5 text-green-600 dark:text-green-500" />
              </div>
              <span className="wrap-break-word">{t("points.noProfile")}</span>
            </div>
            <div className="flex items-center gap-3 text-sm font-medium text-foreground/90">
              <div className="h-6 w-6 rounded-full bg-green-500/10 flex items-center justify-center shrink-0">
                <Banknote className="h-3.5 w-3.5 text-green-600 dark:text-green-500" />
              </div>
              <span className="wrap-break-word">{t("points.noSale")}</span>
            </div>
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row gap-3 sm:justify-between items-center">
            <div className="text-xs text-muted-foreground text-center sm:text-left flex items-center">
              <Link
                href="/privacy"
                className="hover:text-foreground underline underline-offset-4 transition-colors"
                onClick={() => setOpen(false)}
              >
                {t("privacyLink")}
              </Link>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto items-stretch sm:items-center">
              <Button
                variant="ghost"
                onClick={handleReject}
                className="w-full sm:w-auto text-muted-foreground hover:text-foreground"
              >
                {t("reject")}
              </Button>
              <Button
                onClick={handleAccept}
                className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground shadow-sm sm:px-8"
              >
                {t("accept")}
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
